import { ReconciliationStatusBadge } from "@/components/reconciliation/reconciliation-status-badge";
import {
  formatCurrency,
  formatDate,
  formatRunId,
  getRunTitle,
} from "@/components/reconciliation/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Input,
} from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { buildMetadata } from "@/lib/metadata";
import { ReconciliationService } from "@/services/reconciliation-service";
import { UserProfileService } from "@/services/user-profile-service";
import type { ReconciliationRun } from "@/services/types/reconciliation";
import { useQuery } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, MoreVertical, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const PAGE_SIZE = 10;

export const Route = createFileRoute("/_main/reconciliation/")({
  beforeLoad: async () => {
    const profile = await UserProfileService.getMine();
    if (!profile?.is_financial_reconciliation_enabled) {
      throw redirect({ to: "/" });
    }
  },
  component: RouteComponent,
  head: () => ({
    meta: buildMetadata({
      title: "Financial Reconciliation",
      description: "Track reconciliation runs, review exceptions, and approve completed runs.",
      keywords: ["reconciliation", "invoices", "bank statements", "finance"],
    }),
  }),
});

function MetricCard({
  label,
  value,
  detail,
  dotClassName,
}: {
  label: string;
  value: string;
  detail: string;
  dotClassName: string;
}) {
  return (
    <Card className="rounded-[24px] border-slate-200 py-0 shadow-none">
      <CardHeader className="px-5 pt-5">
        <CardTitle className="flex items-center gap-2 text-base text-slate-500">
          <span className={`size-2 rounded-full ${dotClassName}`} />
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        <div className="text-4xl tracking-tight">{value}</div>
        <p className="mt-2 text-sm text-slate-400">{detail}</p>
      </CardContent>
    </Card>
  );
}

function normalizeRunStatusFilter(status: string) {
  if (
    [
      "draft",
      "processing",
      "invoice_processing",
      "uploading_bank_statements",
      "bank_statement_processing",
      "bank_statements_uploaded",
    ].includes(status)
  ) {
    return "processing";
  }

  if (["pending_bank_statements", "ready_to_reconcile"].includes(status)) {
    return "extracted";
  }

  if (status === "ready_for_approval") {
    return "ready";
  }

  return status;
}

function buildPageItems(currentPage: number, totalPages: number) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [
      1,
      "ellipsis",
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }

  return [
    1,
    "ellipsis-left",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "ellipsis-right",
    totalPages,
  ];
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function RouteComponent() {
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");
  const [page, setPage] = useState(1);

  const { data: runs = [], isLoading } = useQuery({
    queryKey: ["reconciliation-runs"],
    queryFn: () => ReconciliationService.listRuns(),
    refetchInterval: (query) => {
      const items = query.state.data ?? [];
      const activeStatuses = new Set(items.map((run) => run.run_status));
      return [...activeStatuses].some((status) =>
        [
          "uploading_bank_statements",
          "bank_statement_processing",
          "processing",
          "invoice_processing",
        ].includes(status),
      )
        ? 5000
        : false;
    },
  });

  const visibleRuns = useMemo(
    () =>
      runs.filter(
        (run) =>
          run.counts.invoices > 0 ||
          !["draft", "invoice_processing"].includes(run.run_status),
      ),
    [runs],
  );

  const filteredRuns = useMemo(() => {
    const today = Date.now();
    return visibleRuns.filter((run) => {
      const searchTarget =
        `${getRunTitle(run)} ${formatRunId(run.id)} ${run.run_status}`.toLowerCase();
      if (search && !searchTarget.includes(search.toLowerCase())) {
        return false;
      }
      if (tab === "review" && run.counts.needs_review === 0) {
        return false;
      }
      if (
        statusFilter !== "all" &&
        normalizeRunStatusFilter(run.run_status) !== statusFilter
      ) {
        return false;
      }
      if (timeFilter !== "all") {
        const uploaded = new Date(run.date_uploaded).getTime();
        const ageDays = (today - uploaded) / (1000 * 60 * 60 * 24);
        if (timeFilter === "7d" && ageDays > 7) {
          return false;
        }
        if (timeFilter === "30d" && ageDays > 30) {
          return false;
        }
      }
      return true;
    });
  }, [search, statusFilter, tab, timeFilter, visibleRuns]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, tab, timeFilter]);

  const totalPages = Math.max(Math.ceil(filteredRuns.length / PAGE_SIZE), 1);
  const currentPage = Math.min(page, totalPages);

  useEffect(() => {
    if (page !== currentPage) {
      setPage(currentPage);
    }
  }, [currentPage, page]);

  const paginatedRuns = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredRuns.slice(start, start + PAGE_SIZE);
  }, [currentPage, filteredRuns]);

  const pageItems = useMemo(
    () => buildPageItems(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const summary = useMemo(() => {
    const invoiceTotal = visibleRuns.reduce(
      (sum, run) => sum + Number(run.totals.invoice_total || 0),
      0,
    );
    const invoicesProcessed = visibleRuns.reduce(
      (sum, run) => sum + run.counts.invoices,
      0,
    );
    const needsReview = visibleRuns.reduce(
      (sum, run) => sum + Number(run.counts.needs_review > 0),
      0,
    );
    return {
      totalRuns: visibleRuns.length,
      invoicesProcessed,
      invoiceTotal,
      needsReview,
    };
  }, [visibleRuns]);

  return (
    <div className="space-y-8 p-4 pb-24 lg:p-8 lg:pb-8">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div className="space-y-2">
          <p className="hidden text-sm text-slate-500 lg:block">Operations / Reconciliation</p>
          <h1 className="text-center text-3xl tracking-tight lg:text-left lg:text-4xl">
            Financial Reconciliation
          </h1>
          <p className="max-w-3xl text-center text-slate-500 lg:text-left">
            Upload vendor invoices, auto-extract the details, then reconcile each
            run against your records.
          </p>
        </div>
        <Button
          asChild
          className="hidden h-12 rounded-xl bg-black px-6 text-white hover:bg-black/90 lg:inline-flex"
        >
          <Link to="/reconciliation/new">
            <Plus className="size-4" />
            New reconciliation run
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <MetricCard
          label="Total runs"
          value={String(summary.totalRuns)}
          detail="All reconciliation runs"
          dotClassName="bg-black"
        />
        <MetricCard
          label="Invoices processed"
          value={summary.invoicesProcessed.toLocaleString()}
          detail="Across all runs"
          dotClassName="bg-emerald-500"
        />
        <MetricCard
          label="Reconciled value"
          value={formatCurrency(summary.invoiceTotal, "SAR")}
          detail="Run totals captured"
          dotClassName="bg-amber-700"
        />
        <MetricCard
          label="Needs review"
          value={String(summary.needsReview)}
          detail="Runs with unresolved invoices"
          dotClassName="bg-rose-500"
        />
      </div>

      <Card className="rounded-none border-0 bg-transparent py-0 shadow-none">
        <CardHeader className="px-6 pt-6">
          <div className="flex flex-col gap-4">
            <div className="border-b border-slate-200">
              <div className="flex items-end gap-8">
                {[
                  { value: "all", label: "All runs" },
                  { value: "review", label: "Needs review" },
                ].map((item) => {
                  const active = tab === item.value;

                  return (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setTab(item.value)}
                      className={`relative pb-3 text-base transition-colors ${
                        active
                          ? "font-medium text-slate-950"
                          : "text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      {item.label}
                      <span
                        className={`absolute inset-x-0 bottom-0 h-0.5 rounded-full transition-opacity ${
                          active ? "bg-foreground opacity-100" : "opacity-0"
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="relative w-full xl:max-w-md">
                <Search className="-translate-y-1/2 absolute top-1/2 left-3 size-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search runs by ID or vendor"
                  className="h-12 rounded-xl border-slate-200 pl-10"
                />
              </div>
              <div className="hidden flex-col gap-3 sm:flex-row lg:flex">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-12 min-w-44 rounded-xl border-slate-200">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="ready">Ready</SelectItem>
                    <SelectItem value="in_review">In review</SelectItem>
                    <SelectItem value="extracted">Extracted</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={timeFilter} onValueChange={setTimeFilter}>
                  <SelectTrigger className="h-12 min-w-36 rounded-xl border-slate-200">
                    <SelectValue placeholder="All time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All time</SelectItem>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="px-6 pb-6">
          <div className="mb-4 text-sm text-slate-500">
            Showing {paginatedRuns.length} of {filteredRuns.length} runs
          </div>

          <div className="space-y-3 lg:hidden">
            {isLoading ? (
              <div className="rounded-[20px] border border-slate-200 px-5 py-16 text-center text-slate-400">
                Loading reconciliation runs...
              </div>
            ) : paginatedRuns.length === 0 ? (
              <div className="rounded-[20px] border border-slate-200 px-5 py-16 text-center text-slate-400">
                No reconciliation runs found.
              </div>
            ) : (
              paginatedRuns.map((run) => <RunMobileCard key={String(run.id)} run={run} />)
            )}
          </div>
          <div className="hidden overflow-hidden rounded-[20px] border border-slate-200 bg-white lg:block">
            <table className="w-full min-w-[980px] bg-white">
              <thead className="bg-white text-left text-sm text-slate-500">
                <tr className="border-b border-slate-200">
                  <th className="px-5 py-3.5 font-medium">Run</th>
                  <th className="px-5 py-3.5 font-medium">Run ID</th>
                  <th className="px-5 py-3.5 font-medium">Invoices</th>
                  <th className="px-5 py-3.5 font-medium">Amount</th>
                  <th className="px-5 py-3.5 font-medium">Status</th>
                  <th className="px-5 py-3.5 font-medium">Matched</th>
                  <th className="px-5 py-3.5 font-medium">Started</th>
                  <th className="px-5 py-3.5 font-medium" />
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td className="px-5 py-16 text-center text-slate-400" colSpan={8}>
                      Loading reconciliation runs...
                    </td>
                  </tr>
                ) : paginatedRuns.length === 0 ? (
                  <tr>
                    <td className="px-5 py-16 text-center text-slate-400" colSpan={8}>
                      No reconciliation runs found.
                    </td>
                  </tr>
                ) : (
                  paginatedRuns.map((run) => <RunRow key={String(run.id)} run={run} />)
                )}
              </tbody>
            </table>
            {!isLoading && filteredRuns.length > 0 ? (
              <div className="flex items-center justify-between border-t border-slate-200 px-5 py-4">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 rounded-xl border-slate-200 px-4"
                  onClick={() => setPage((value) => Math.max(value - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="size-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  {pageItems.map((item, index) =>
                    typeof item === "number" ? (
                      <button
                        key={`${item}-${index}`}
                        type="button"
                        onClick={() => setPage(item)}
                        className={`flex size-10 items-center justify-center rounded-lg transition ${
                          item === currentPage
                            ? "bg-[#FDEB7D] text-[#2D0909] shadow-sm"
                            : "text-slate-500 hover:bg-slate-100"
                        }`}
                      >
                        {item}
                      </button>
                    ) : (
                      <span key={`${item}-${index}`} className="px-1 text-slate-400">
                        ...
                      </span>
                    ),
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 rounded-xl border-slate-200 px-4"
                  onClick={() =>
                    setPage((value) => Math.min(value + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <div className="fixed right-4 bottom-4 left-4 z-50 pb-[max(env(safe-area-inset-bottom),0px)] lg:hidden">
        <Button
          asChild
          className="h-12 w-full rounded-xl bg-black text-white hover:bg-black/90"
        >
          <Link to="/reconciliation/new">
            <Plus className="size-4" />
            New reconciliation run
          </Link>
        </Button>
      </div>
    </div>
  );
}

function RunRow({ run }: { run: ReconciliationRun }) {
  const navigate = useNavigate();
  const activeInvoices = Math.max(run.counts.active_invoices, 0);
  const params = { runId: String(run.id) };

  return (
    <tr
      className="cursor-pointer border-t border-slate-200 text-sm transition-colors hover:bg-slate-50"
      role="link"
      tabIndex={0}
      onClick={() =>
        navigate({
          to: "/reconciliation/$runId",
          params,
        })
      }
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          navigate({
            to: "/reconciliation/$runId",
            params,
          });
        }
      }}
    >
      <td className="px-5 py-4 align-middle">
        <div className="space-y-1">
          <div className="font-medium text-slate-950">{getRunTitle(run)}</div>
          <div className="text-sm text-slate-400">
            by You · {formatTime(run.date_uploaded)}
          </div>
        </div>
      </td>
      <td className="px-5 py-4 text-slate-600">{formatRunId(run.id)}</td>
      <td className="px-5 py-4">
        <span className="inline-flex min-w-9 items-center justify-center rounded-lg border border-slate-200 px-2.5 py-1 text-slate-700">
          {run.counts.invoices}
        </span>
      </td>
      <td className="px-5 py-4 font-medium text-slate-950">
        {formatCurrency(run.totals.invoice_total, "SAR")}
      </td>
      <td className="px-5 py-4">
        <ReconciliationStatusBadge status={run.run_status} />
      </td>
      <td className="px-5 py-4 text-slate-600">
        {run.counts.matched} / {activeInvoices}
      </td>
      <td className="px-5 py-4 text-slate-600">{formatDate(run.date_uploaded)}</td>
      <td className="px-5 py-4 text-right">
        <Link
          to="/reconciliation/$runId"
          params={params}
          onClick={(event) => event.stopPropagation()}
          className="inline-flex size-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
        >
          <MoreVertical className="size-4" />
        </Link>
      </td>
    </tr>
  );
}

function RunMobileCard({ run }: { run: ReconciliationRun }) {
  const activeInvoices = Math.max(run.counts.active_invoices, 0);

  return (
    <Link
      to="/reconciliation/$runId"
      params={{ runId: String(run.id) }}
      className="block rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-xl font-medium text-slate-900">
            {getRunTitle(run)}
          </div>
          <div className="mt-1 text-sm text-slate-400">
            {formatDate(run.date_uploaded)}
          </div>
        </div>
        <ReconciliationStatusBadge status={run.run_status} />
      </div>
      <div className="mt-4 border-t border-slate-100 pt-4">
        <div className="flex items-center justify-between gap-3 text-sm">
          <div className="flex gap-2 text-slate-500">
            <span className="rounded-md border border-slate-200 px-2 py-1">
              {formatRunId(run.id)}
            </span>
            <span className="rounded-md border border-slate-200 px-2 py-1">
              {run.counts.invoices} inv
            </span>
          </div>
          <div className="font-medium text-slate-900">
            {run.counts.matched}/{activeInvoices}
          </div>
          <div className="text-slate-500">
            {formatCurrency(run.totals.invoice_total, "SAR")}
          </div>
        </div>
      </div>
    </Link>
  );
}
